import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileSpreadsheet, ChevronRight, Check, X, ArrowRight, Database, Trash2, RotateCcw, ChevronDown, Loader2 } from 'lucide-react';
import { uploadPreview, uploadCommit, getUploadStatus, resetData, FIELD_LABELS, REQUIRED_FIELDS } from '../api';

function StepBadge({ n, active, done }) {
  const cls = done ? 'bg-green-500 text-white' : active ? 'bg-blue-600 text-white' : 'bg-white/10 text-slate-400';
  return (
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${cls}`}>
      {done ? <Check size={14} /> : n}
    </div>
  );
}

function FileDropZone({ onFile, disabled, label }) {
  const [drag, setDrag] = useState(false);
  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0]; if (f) onFile(f);
  };
  return (
    <label
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      className={`panel border-2 border-dashed cursor-pointer flex flex-col items-center justify-center py-10 px-6 transition-colors ${drag ? 'border-blue-500 bg-blue-500/5' : 'border-white/10 hover:border-white/20'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      <input type="file" accept=".xlsx,.xls" disabled={disabled} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
      <FileSpreadsheet size={40} className="text-blue-400 mb-3" />
      <div className="text-slate-200 text-sm font-semibold">{label}</div>
      <div className="text-slate-500 text-xs mt-1">اسحب وأفلت الملف أو اضغط للاختيار • XLSX / XLS</div>
    </label>
  );
}

function MappingEditor({ preview, mapping, setMapping, kind }) {
  const required = REQUIRED_FIELDS[kind];
  const fields = preview.fields;
  const columns = ['', ...preview.columns];
  return (
    <div className="space-y-2">
      {fields.map((f) => {
        const isReq = required.includes(f);
        const val = mapping[f] || '';
        const missing = isReq && !val;
        return (
          <div key={f} className={`panel px-3 py-2 flex items-center gap-3 ${missing ? 'border-red-500/40' : ''}`}>
            <div className="w-56 text-right">
              <div className="text-slate-200 text-[13px] font-semibold">{FIELD_LABELS[f] || f}</div>
              {isReq && <div className="text-red-400 text-[10px]">حقل مطلوب</div>}
            </div>
            <div className="flex-1 relative">
              <select
                value={val}
                onChange={(e) => setMapping({ ...mapping, [f]: e.target.value || null })}
                className="w-full appearance-none bg-[#0B1424] border border-white/10 rounded-lg px-3 py-2 text-slate-200 text-[13px] pr-8 focus:outline-none focus:border-blue-500"
              >
                <option value="">— لا توجد مطابقة —</option>
                {columns.slice(1).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <ChevronDown size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
            {val && preview.preview.length > 0 && (
              <div className="w-36 text-slate-500 text-[11px] truncate" title={preview.preview[0][val] || ''}>
                {preview.preview[0][val] || '—'}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DataCard({ label, count, tint, icon: Icon, last, onDelete }) {
  const at = last?.at ? new Date(last.at).toLocaleString('en-GB', { hour12: false }) : null;
  return (
    <div className="panel p-4">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-slate-500 text-[11.5px] font-semibold">{label}</div>
          <div className="kpi-number text-[26px] mt-1">{(count || 0).toLocaleString('en-US')}</div>
        </div>
        <div className="icon-chip" style={{ background: `${tint}18`, color: tint, border: `1px solid ${tint}30` }}>
          <Icon size={16} strokeWidth={1.75} />
        </div>
      </div>
      <div className="mt-3 pt-3 border-t hair text-[11px] space-y-0.5">
        {last?.filename ? (
          <>
            <div className="text-slate-500 truncate" title={last.filename}>الملف: <span className="text-slate-100 font-semibold">{last.filename}</span></div>
            <div className="text-slate-500">آخر تحديث: <span className="text-slate-100 font-semibold">{at || '—'}</span></div>
          </>
        ) : (
          <div className="text-slate-500">لم يتم استيراد ملف بعد</div>
        )}
      </div>
      {onDelete && (
        <button onClick={onDelete} className="mt-3 w-full btn btn-danger text-[11.5px] py-1.5">
          <Trash2 size={12} /> حذف البيانات
        </button>
      )}
    </div>
  );
}

export default function UploadPage() {
  const nav = useNavigate();
  const [kind, setKind] = useState('schools');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { token, columns, preview, fields, suggested_mapping }
  const [mapping, setMapping] = useState({});
  const [message, setMessage] = useState(null);
  const [status, setStatus] = useState({ schools_count: 0, tickets_count: 0, supply_count: 0, mappings: {}, last_import: {} });
  // Ingest mode. Supply defaults to merge (append + upsert); others default to replace.
  const [mode, setMode] = useState('replace');

  useEffect(() => {
    // Schools + Supply default to Merge (append + dedup); Tickets to Replace.
    setMode(kind === 'tickets' ? 'replace' : 'append');
  }, [kind]);

  const refreshStatus = async () => {
    try { setStatus(await getUploadStatus()); } catch (e) {}
  };
  useEffect(() => { refreshStatus(); }, []);

  const onFile = async (file) => {
    setBusy(true); setMessage(null); setPreview(null);
    try {
      const data = await uploadPreview(kind, file);
      setPreview(data);
      setMapping(data.suggested_mapping || {});
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const onImport = async () => {
    const req = REQUIRED_FIELDS[kind];
    for (const r of req) if (!mapping[r]) { setMessage({ type: 'error', text: `يجب تحديد ${FIELD_LABELS[r]}` }); return; }
    // Supply: require at least school_id OR school_name
    if (kind === 'supply' && !mapping.school_id && !mapping.school_name) {
      setMessage({ type: 'error', text: 'يجب مطابقة أحد الحقلين: الرقم الوزاري أو اسم المدرسة' });
      return;
    }
    setBusy(true); setMessage(null);
    try {
      const res = await uploadCommit(preview.token, mapping, mode);
      const label = { schools: 'المدارس', tickets: 'البلاغات', supply: 'التوريد' }[kind] || kind;
      const detail = mode === 'append'
        ? `تم دمج ${res.imported} سجل في ${label} (جديد: ${res.inserted ?? 0} · محدث: ${res.updated ?? 0})`
        : `تم استيراد ${res.imported} سجل إلى ${label} (استبدال كامل)`;
      setMessage({ type: 'success', text: detail });
      setPreview(null); setMapping({});
      await refreshStatus();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const onReset = async (k) => {
    const labels = { schools: 'المدارس', tickets: 'البلاغات', supply: 'التوريد', all: 'كل البيانات' };
    if (!window.confirm(`حذف ${labels[k]}؟`)) return;
    await resetData(k);
    await refreshStatus();
    setMessage({ type: 'success', text: 'تم حذف البيانات' });
  };

  return (
    <div dir="rtl" className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="section-eyebrow">مركز البيانات</div>
            <h1 className="section-title mt-1" style={{ fontSize: 24 }}>استيراد وإدارة ملفات Excel</h1>
            <p className="text-slate-400 text-sm mt-1.5">تعرّف تلقائي على الأعمدة (عربي/إنجليزي). ملفات التوريد تُدمج مع البيانات السابقة تلقائياً بدون تكرار.</p>
          </div>
          <button onClick={() => nav('/')} className="btn btn-ghost">
            <ArrowRight size={14} /> العودة إلى لوحة التحكم
          </button>
        </div>

        {/* Data Center — per-module cards with last-import metadata */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          <DataCard label="المدارس" count={status.schools_count} tint="#3B82F6" icon={Database}
            last={status.last_import?.schools} onDelete={() => onReset('schools')} />
          <DataCard label="البلاغات" count={status.tickets_count} tint="#F59E0B" icon={FileSpreadsheet}
            last={status.last_import?.tickets} onDelete={() => onReset('tickets')} />
          <DataCard label="التوريد" count={status.supply_count} tint="#A78BFA" icon={FileSpreadsheet}
            last={status.last_import?.supply} onDelete={() => onReset('supply')} />
        </div>

        {/* Kind tabs */}
        <div className="panel p-2 inline-flex gap-1 mb-4">
          {[
            { id: 'schools', label: 'ملف المدارس' },
            { id: 'tickets', label: 'ملف البلاغات' },
            { id: 'supply',  label: 'ملف التوريد' },
          ].map((t) => (
            <button key={t.id} onClick={() => { setKind(t.id); setPreview(null); setMapping({}); setMessage(null); }}
              data-testid={`upload-tab-${t.id}`}
              className={`px-4 py-2 rounded-lg text-[13px] font-semibold transition-colors ${kind === t.id ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-white/5'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Steps */}
        <div className="flex items-center gap-3 mb-4 text-sm">
          <div className="flex items-center gap-2"><StepBadge n={1} active={!preview} done={!!preview} /> <span className="text-slate-300">رفع الملف</span></div>
          <ChevronRight size={14} className="text-slate-500 rtl:rotate-180" />
          <div className="flex items-center gap-2"><StepBadge n={2} active={!!preview} /> <span className="text-slate-300">مطابقة الأعمدة</span></div>
          <ChevronRight size={14} className="text-slate-500 rtl:rotate-180" />
          <div className="flex items-center gap-2"><StepBadge n={3} /> <span className="text-slate-300">حفظ البيانات</span></div>
        </div>

        {message && (
          <div className={`panel p-3 mb-4 flex items-center gap-2 ${message.type === 'error' ? 'border-red-500/40' : 'border-green-500/40'}`}>
            {message.type === 'error' ? <X size={16} className="text-red-400" /> : <Check size={16} className="text-green-400" />}
            <span className={`text-sm ${message.type === 'error' ? 'text-red-300' : 'text-green-300'}`}>{message.text}</span>
          </div>
        )}

        {!preview && (
          <FileDropZone onFile={onFile} disabled={busy}
            label={{ schools: 'ارفع ملف المدارس (Excel)', tickets: 'ارفع ملف البلاغات (Excel)', supply: 'ارفع ملف التوريد (Excel)' }[kind]} />
        )}
        {busy && !preview && (
          <div className="mt-4 text-slate-400 text-sm flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> جاري قراءة الملف...</div>
        )}

        {preview && (
          <div className="space-y-4">
            <div className="panel p-3 text-sm text-slate-300 flex items-center gap-2">
              <FileSpreadsheet size={16} className="text-blue-400" />
              <span className="font-semibold">{preview.filename}</span>
              <span className="text-slate-500">•</span>
              <span>{preview.row_count.toLocaleString('en-US')} صف</span>
              <span className="text-slate-500">•</span>
              <span>{preview.columns.length} عمود</span>
            </div>

            <div>
              <div className="text-slate-200 text-sm font-semibold mb-2">معاينة أول صفوف</div>
              <div className="panel overflow-x-auto">
                <table className="w-full text-[11.5px] text-slate-300">
                  <thead>
                    <tr className="bg-white/[0.03]">
                      {preview.columns.map((c) => <th key={c} className="px-3 py-2 text-right whitespace-nowrap font-semibold text-slate-200">{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((r, i) => (
                      <tr key={i} className="border-t border-white/5">
                        {preview.columns.map((c) => <td key={c} className="px-3 py-1.5 whitespace-nowrap text-slate-400">{r[c] ?? ''}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="text-slate-200 text-sm font-semibold mb-2">مطابقة الأعمدة (تم التعرف تلقائياً)</div>
              <MappingEditor preview={preview} mapping={mapping} setMapping={setMapping} kind={kind} />
            </div>

            <div className="flex items-center gap-2 justify-between flex-wrap">
              <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1" data-testid="upload-mode-toggle">
                <button onClick={() => setMode('append')}
                  className={`px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors ${mode === 'append' ? 'bg-white shadow-sm text-white' : 'text-slate-500 hover:text-slate-100'}`}
                  title="دمج مع البيانات الحالية · تحديث المكرر · إضافة الجديد">
                  دمج (Append)
                </button>
                <button onClick={() => setMode('replace')}
                  className={`px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors ${mode === 'replace' ? 'bg-white shadow-sm text-white' : 'text-slate-500 hover:text-slate-100'}`}
                  title="حذف الكامل واستبداله بهذا الملف">
                  استبدال كامل (Replace)
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button onClick={() => { setPreview(null); setMapping({}); }} className="btn btn-ghost">إلغاء</button>
                <button onClick={onImport} disabled={busy} data-testid="upload-commit-btn" className="btn btn-primary disabled:opacity-50">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {mode === 'append' ? 'دمج البيانات في القاعدة' : 'حفظ البيانات (استبدال)'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
